import {Component, ElementRef, HostListener, OnDestroy, ViewChild} from '@angular/core';
import {Observable, Subject, forkJoin, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import {AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent} from 'primeng/autocomplete';
import {ResultListApiModel} from '../../../../api/models/result-list.api.model';
import {UserApiModel} from '../../../../api/models/user.api-model';
import {ChatAttachmentApiModel} from '../../../../api/models/chat-attachment.api-model';
import {MediaObjectApiModel} from '../../../../api/models/media-object.api-model';
import {QueryParamsApiModel} from '../../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../../api/models/filter-parameter.api.model';
import {UserRestService} from '../../../../api/services/user.rest.service';
import {MediaObjectRestService} from '../../../../api/services/media-object.rest.service';
import {ChatDataProviderService} from '../../../../features/chat/chat.data-provider.service';
import {ResponseNotificationService} from '../../../../core/services/response-notification.service';
import {CurrentUserViewModel} from '../../../../core/models/current-user.view-model';
import {ModalInterface} from '../../modal.interface';
import {ModalState} from '../../modal-state.enum';
import {ModalStateEvent} from '../../modal-state-event';
import {PostViewModel} from '../../../timeline/post.view-model';
import {TranslateService} from '@ngx-translate/core';
import {ShareCardApiModel} from '../../../../api/models/share-card.api-model';
import {SocialInputComponent} from '../../../social/social-input/social-input.component';
import {SocialInputSubmitInterface} from '../../../social/social-input/social-input-submit.interface';
import {map, switchMap} from 'rxjs/operators';

interface ChatShareRecipientOption {
  id: string;
  alias: string;
  user: UserApiModel;
}

@Component({
  selector: 'app-chat-share-modal',
  templateUrl: './chat-share-modal.component.html',
  styleUrls: ['./chat-share-modal.component.scss'],
})
export class ChatShareModalComponent implements ModalInterface, OnDestroy {
  private readonly fullscreenBreakpoint = 1024;

  public data: any;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public currentUserVM: CurrentUserViewModel | null = null;
  public sharedPost: PostViewModel | null = null;
  public recipientResults: ChatShareRecipientOption[] = [];
  public selectedRecipients: ChatShareRecipientOption[] = [];
  public desktopAutocompleteValue: ChatShareRecipientOption[] = [];
  public submitting = false;
  public recipientTotal = 0;
  public availableRecipientTotal = 0;
  public hasResolvedInitialRecipientLoad = false;
  public hasResolvedRecipientQuery = false;
  public isFullscreenRecipientLayout = false;
  public loadingInitialRecipients = false;
  public loadingMoreRecipients = false;
  public hasMoreRecipients = false;

  @ViewChild('messageInput') public messageInput?: SocialInputComponent;
  @ViewChild('desktopRecipientsAutocomplete') public desktopRecipientsAutocomplete?: AutoComplete;
  @ViewChild('mobileRecipientScroller') public mobileRecipientScroller?: ElementRef<HTMLElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly recipientQuery$ = new Subject<string>();
  public currentRecipientQuery = '';
  private readonly recipientLimit = 20;
  private currentRecipientPage = 0;
  private recipientRequestId = 0;

  constructor(
    private readonly userRS: UserRestService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly chatDPS: ChatDataProviderService,
    private readonly translate: TranslateService,
    private readonly rns: ResponseNotificationService,
  ) {
  }

  ngOnInit(): void {
    this.currentUserVM = this.data?.currentUserVM || null;
    this.sharedPost = this.resolveSharedPost(this.data?.sharedPost || null);
    this.updateRecipientLayoutMode();
    this.setupRecipientSearch();
    if (this.isFullscreenRecipientLayout) {
      this.queueRecipientsLoad(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    const wasFullscreenLayout = this.isFullscreenRecipientLayout;
    this.updateRecipientLayoutMode();
    if (!wasFullscreenLayout && this.isFullscreenRecipientLayout && !this.hasResolvedInitialRecipientLoad) {
      this.queueRecipientsLoad(true);
    }
  }

  public submit(): void {
    if (this.submitting || !this.sharedPost?.id || !this.selectedRecipients.length) {
      return;
    }

    this.messageInput?.onInputSubmitClick({allowEmptyContent: true});
  }

  public onMessageSubmit(event: SocialInputSubmitInterface): void {
    if (this.submitting || !this.sharedPost?.id || !this.selectedRecipients.length) {
      return;
    }

    const attachments = (event?.mediaObjectAMs || []) as ChatAttachmentApiModel[];
    const attachmentIris = attachments
      .map((attachment) => this.getAttachmentIri(attachment))
      .filter((attachmentIri): attachmentIri is string => !!attachmentIri);
    const normalizedContent = this.normalizeContent(event?.content || '');
    const requests = this.selectedRecipients
      .filter((recipient) => !!recipient?.user?.['@id'])
      .map((recipient) => this.chatDPS.createMessage({
        recipient: recipient.user['@id'],
        content: normalizedContent,
        attachments: attachmentIris.length ? attachmentIris : undefined,
        messageType: 'share',
        shareType: 'post',
        shareId: String(this.sharedPost?.id),
      }));

    if (!requests.length) {
      return;
    }

    this.submitting = true;
    forkJoin(requests).subscribe({
      next: (messages) => {
        this.submitting = false;
        event?.input?.reset();
        this.selectedRecipients = [];
        this.rns.emitMessage(this.translate.instant('shared.share.chat.success', {count: messages.length}));
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, messages));
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  public onRecipientSearch(event: AutoCompleteCompleteEvent | {query?: string} | string): void {
    const query = typeof event === 'string'
      ? event
      : (event?.query || '');

    this.currentRecipientQuery = query.trim();
    this.recipientQuery$.next(this.currentRecipientQuery);
  }

  public onRecipientFocus(): void {
    if (this.hasResolvedInitialRecipientLoad || this.isRecipientLoadPending) {
      return;
    }

    this.queueRecipientsLoad(true);
  }

  public onMobileRecipientSearch(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.onRecipientSearch(target?.value || '');
  }

  public onRecipientSelected(event: AutoCompleteSelectEvent): void {
    const selected = event?.value as ChatShareRecipientOption | null;
    if (!selected) {
      return;
    }

    this.addRecipient(selected);
    this.clearDesktopAutocomplete();
  }

  public onMobileResultsScroll(): void {
    if (!this.isFullscreenRecipientLayout || !this.hasMoreRecipients || this.loadingMoreRecipients || this.loadingInitialRecipients) {
      return;
    }

    const scroller = this.mobileRecipientScroller?.nativeElement;
    if (!scroller) {
      return;
    }

    const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (remaining <= 64) {
      this.queueRecipientsLoad(false);
    }
  }

  public toggleRecipient(recipient: ChatShareRecipientOption): void {
    if (this.isRecipientSelected(recipient)) {
      this.removeRecipient(recipient);
      return;
    }

    this.addRecipient(recipient);
  }

  public removeRecipient(recipient: ChatShareRecipientOption): void {
    this.selectedRecipients = this.selectedRecipients.filter((selected) => selected.id !== recipient.id);
  }

  public isRecipientSelected(recipient: ChatShareRecipientOption | null): boolean {
    if (!recipient?.id) {
      return false;
    }

    return this.selectedRecipients.some((selected) => selected.id === recipient.id);
  }

  public recipientAvatar(option: ChatShareRecipientOption | null): MediaObjectApiModel | null {
    const avatar = option?.user?.avatar;

    if (!avatar || typeof avatar === 'string') {
      return null;
    }

    return avatar;
  }

  public get shouldShowEmptyRecipientsState(): boolean {
    return this.hasResolvedInitialRecipientLoad
      && !this.isRecipientLoadPending
      && this.availableRecipientTotal === 0
      && !this.selectedRecipients.length;
  }

  public get shouldShowRecipientQueryEmptyState(): boolean {
    return !!this.currentRecipientQuery
      && this.hasResolvedRecipientQuery
      && !this.isRecipientLoadPending
      && !this.recipientResults.length;
  }

  public get desktopRecipientSuggestions(): ChatShareRecipientOption[] {
    return this.recipientResults.filter((option) => !this.isRecipientSelected(option));
  }

  public get shouldShowSelectedRecipients(): boolean {
    return this.selectedRecipients.length > 0;
  }

  public get isRecipientLoadPending(): boolean {
    return this.loadingInitialRecipients || this.loadingMoreRecipients;
  }

  public get previewCard(): ShareCardApiModel | null {
    if (!this.sharedPost) {
      return null;
    }

    return {
      type: 'post',
      available: true,
      post: this.sharedPost.apiModel || this.sharedPost,
    } as ShareCardApiModel;
  }

  private setupRecipientSearch(): void {
    this.recipientQuery$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((query) => {
      this.currentRecipientQuery = query;
      this.loadRecipients(true);
    });
  }

  private loadRecipients(reset: boolean): void {
    if (!this.currentUserVM?.['@id']) {
      this.applyRecipientsResult(this.createEmptyRecipientsResult(), reset);
      return;
    }

    const requestId = ++this.recipientRequestId;
    const page = reset ? 1 : (this.currentRecipientPage + 1);
    if (reset) {
      this.loadingInitialRecipients = true;
      this.loadingMoreRecipients = false;
    } else {
      this.loadingMoreRecipients = true;
    }

    this.userRS.getShareChatRecipientsSubResourceCollection(this.currentUserVM['@id'], {
      q: this.currentRecipientQuery,
      page,
      limit: this.recipientLimit,
    }).pipe(
      switchMap((result) => this.hydrateRecipientAvatarUsers(result)),
      catchError(() => of(this.createEmptyRecipientsResult())),
      finalize(() => {
        if (requestId !== this.recipientRequestId) {
          return;
        }

        this.loadingInitialRecipients = false;
        this.loadingMoreRecipients = false;
      })
    ).subscribe((result) => {
      if (requestId !== this.recipientRequestId) {
        return;
      }

      this.currentRecipientPage = page;
      this.applyRecipientsResult(result, reset);
    });
  }

  private applyRecipientsResult(result: ResultListApiModel<UserApiModel>, reset: boolean): void {
    const isInitialQuery = this.currentRecipientQuery === '';
    const hydratedUsers = result.records || [];
    const nextResults = hydratedUsers
      .map((user) => this.createRecipientOption(user))
      .filter((option): option is ChatShareRecipientOption => !!option);
    const refreshedSelectedRecipients = this.refreshSelectedRecipients(hydratedUsers);

    this.hasResolvedRecipientQuery = true;
    if (isInitialQuery) {
      this.hasResolvedInitialRecipientLoad = true;
      this.availableRecipientTotal = result?.total || 0;
    }

    this.recipientTotal = result?.total || 0;
    this.selectedRecipients = refreshedSelectedRecipients;
    this.recipientResults = reset
      ? nextResults
      : this.uniqueRecipients([...this.recipientResults, ...nextResults]);
    this.hasMoreRecipients = this.recipientResults.length < this.recipientTotal;
  }

  private queueRecipientsLoad(reset: boolean): void {
    if (reset) {
      this.currentRecipientPage = 0;
      this.recipientQuery$.next(this.currentRecipientQuery);
      return;
    }

    this.loadRecipients(false);
  }

  private clearDesktopAutocomplete(): void {
    this.desktopAutocompleteValue = [];
    this.desktopRecipientsAutocomplete?.writeValue(null);
  }

  private addRecipient(recipient: ChatShareRecipientOption): void {
    this.selectedRecipients = this.uniqueRecipients([...this.selectedRecipients, recipient]);
  }

  private updateRecipientLayoutMode(): void {
    this.isFullscreenRecipientLayout = typeof window !== 'undefined'
      ? window.innerWidth < this.fullscreenBreakpoint
      : false;
  }

  private createRecipientOption(user: UserApiModel | null): ChatShareRecipientOption | null {
    if (!user?.['@id']) {
      return null;
    }

    return {
      id: user['@id'],
      alias: this.recipientLabel(user),
      user,
    };
  }

  private uniqueRecipients(recipients: ChatShareRecipientOption[]): ChatShareRecipientOption[] {
    const unique = new Map<string, ChatShareRecipientOption>();
    recipients.forEach((recipient) => {
      if (!recipient?.id || unique.has(recipient.id)) {
        return;
      }

      unique.set(recipient.id, recipient);
    });

    return Array.from(unique.values());
  }

  private hydrateRecipientAvatarUsers(result: ResultListApiModel<UserApiModel>): Observable<ResultListApiModel<UserApiModel>> {
    const users = result.records || [];
    const avatarReferences = Array.from(new Set(
      users
        .map((user) => this.getAvatarReferenceToHydrate(user))
        .filter((reference): reference is string => !!reference)
    ));

    if (!avatarReferences.length) {
      return of(result);
    }

    const params = new QueryParamsApiModel();
    params.filters = avatarReferences.map((reference) => new FilterParameterApiModel('id', reference));

    return this.mediaObjectRS.getMediaObjectCollection(params).pipe(
      map((mediaResult) => {
        const avatarsByReference = new Map<string, MediaObjectApiModel>();
        (mediaResult.records || []).forEach((avatar) => {
          if (avatar?.['@id']) {
            avatarsByReference.set(avatar['@id'], avatar);
          }

          if (avatar?.id !== null && avatar?.id !== undefined) {
            avatarsByReference.set(String(avatar.id), avatar);
          }
        });

        if (!avatarsByReference.size) {
          return result;
        }

        return {
          ...result,
          records: users.map((user) => this.withHydratedAvatar(user, avatarsByReference))
        } as ResultListApiModel<UserApiModel>;
      }),
      catchError(() => of(result))
    );
  }

  private withHydratedAvatar(
    user: UserApiModel,
    avatarsByReference: Map<string, MediaObjectApiModel>
  ): UserApiModel {
    const avatarReference = this.getAvatarReferenceToHydrate(user);
    if (!avatarReference) {
      return user;
    }

    const hydratedAvatar = avatarsByReference.get(avatarReference);
    if (!hydratedAvatar) {
      return user;
    }

    return {
      ...user,
      avatar: hydratedAvatar,
    } as UserApiModel;
  }

  private getAvatarReferenceToHydrate(user: UserApiModel | null): string | null {
    const avatar = user?.avatar;
    if (!avatar) {
      return null;
    }

    if (typeof avatar === 'string') {
      return avatar;
    }

    if (avatar.hyperlinks?.length) {
      return null;
    }

    if (avatar['@id']) {
      return avatar['@id'];
    }

    if (avatar.id !== null && avatar.id !== undefined) {
      return String(avatar.id);
    }

    return null;
  }

  private refreshSelectedRecipients(hydratedUsers: UserApiModel[]): ChatShareRecipientOption[] {
    if (!this.selectedRecipients.length || !hydratedUsers.length) {
      return this.selectedRecipients;
    }

    const usersById = new Map<string, UserApiModel>();
    hydratedUsers.forEach((user) => {
      if (user?.['@id']) {
        usersById.set(user['@id'], user);
      }
    });

    return this.selectedRecipients.map((recipient) => {
      const hydratedUser = usersById.get(recipient.id);
      if (!hydratedUser) {
        return recipient;
      }

      return {
        ...recipient,
        alias: this.recipientLabel(hydratedUser),
        user: hydratedUser,
      };
    });
  }

  private recipientLabel(user: UserApiModel | null): string {
    if (!user) {
      return '';
    }

    return user.username || user.slug || user['@id'] || '';
  }

  private getAttachmentIri(attachment: ChatAttachmentApiModel | string | null | undefined): string | null {
    if (!attachment) {
      return null;
    }

    if (typeof attachment === 'string') {
      return attachment;
    }

    return attachment['@id'] || null;
  }

  private normalizeContent(content: string): string | null {
    const strippedContent = (content || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();

    return strippedContent ? content : null;
  }

  private createEmptyRecipientsResult(): ResultListApiModel<UserApiModel> {
    return new ResultListApiModel<UserApiModel>(UserApiModel);
  }

  private resolveSharedPost(post: PostViewModel | null): PostViewModel | null {
    return PostViewModel.ensureInstance(post);
  }
}
